import type {SearchAction} from '../context/SearchContext';
import {AirtablePromotionItem} from '../models/airtable';
import {AppRoute} from '../models/AppRoute';
import {Department} from '../models/middleware';
import {navigateToGiftCardsPromotionOrLoadingScreen} from '../queries/promotions';
import type {FilterStore} from '../stores/filterStore';
import type {RangeStore} from '../stores/rangeStore';
import {GIFT_CARDS_TILE, SPECIALS_TILE} from '../stores/rangeStore';

export type OpenDepartmentDeps = {
  rangeStore: RangeStore;
  filterStore: FilterStore;
  /** Absent outside SearchProvider; the destination screen's mount effect then scopes the search. */
  searchDispatch?: (a: SearchAction) => void;
  rawPromotions: AirtablePromotionItem[];
  navigate: (route: AppRoute, params?: object) => void;
};

export type OpenDepartmentTarget = {
  department: Department;
  parent?: Department;
  specials?: boolean;
};

/**
 * The one department press. Shop tiles and the header mega menu both call it, so they
 * cannot diverge (spec 7.3.2). Rules run in this order and the first match returns.
 */
export const openDepartment = (deps: OpenDepartmentDeps, target: OpenDepartmentTarget): void => {
  const {rangeStore, filterStore, searchDispatch, rawPromotions, navigate} = deps;
  const {department, parent, specials} = target;

  // 1. Gift-cards tile: gift-cards navigation, nothing else.
  if (department.id === GIFT_CARDS_TILE) {
    navigateToGiftCardsPromotionOrLoadingScreen(rawPromotions, navigate, false);
    return;
  }

  // 2. Sub-department press.
  if (parent) {
    rangeStore.selectSubDepartment(department);
    navigate(AppRoute.SUB_DEPARTMENT, {department: parent, subDepartment: department});
    return;
  }

  // 3. Specials tile, with or without a specials flag (rule 4 never sees it).
  // 4. A department-scoped Specials link (RP11).
  const isSpecialsTile = department.id === SPECIALS_TILE;
  if (specials && !isSpecialsTile) {
    searchDispatch?.({type: 'SCOPE_TO_DEPARTMENT', departmentId: department.id, specials: true});
    navigate(AppRoute.DEPARTMENT, {department, specials: true});
    return;
  }

  // 5. Top-level department (rule 3 shares this sequence; only the scope differs).
  rangeStore.selectDepartment(department);
  filterStore.clearSortAndFilters();
  if (!rangeStore.getSubDepartments().length) {
    rangeStore.selectSubDepartment(department);
  }
  if (isSpecialsTile) {
    searchDispatch?.({type: 'SCOPE_TO_DEPARTMENT', departmentId: '', specials: true});
  } else {
    searchDispatch?.({type: 'SCOPE_TO_DEPARTMENT', departmentId: department.id});
  }
  navigate(AppRoute.DEPARTMENT, {department});
};
